import { SlidingLabelConfig } from 'components/slidingLabel/slidingLabel';
import { wccModules } from 'enums/wccModules';
import { inject, injectable } from 'inversify';
import { Subscribable, SubscribableOrNullableValue } from 'knockout';
import WCCAttachment from 'models/attachments/attachment';
import WCCFile from 'models/attachments/file';
import './caption.html';
import './caption.scss';

export type WCCAttachmentCaptionVMMode = 'title' | 'file-name'
export type WCCAttachmentCaptionVMPlacement = 'bottom-left' | 'top-left'

export interface WCCAttachmentCaptionVMConfig {
    attachment?: SubscribableOrNullableValue<WCCAttachment>
    mode?: SubscribableOrNullableValue<WCCAttachmentCaptionVMMode>
    placement?: SubscribableOrNullableValue<WCCAttachmentCaptionVMPlacement>
    value?: SubscribableOrNullableValue<string>
    visible?: SubscribableOrNullableValue<boolean>
    slide?: SubscribableOrNullableValue<boolean>
}

@injectable()
export default class WCCAttachmentCaptionVM {
    private attachment: Subscribable<WCCAttachment | undefined>
    private placement: Subscribable<WCCAttachmentCaptionVMPlacement>
    private mode: Subscribable<WCCAttachmentCaptionVMMode>
    private value: Subscribable<string | undefined>

    caption: Subscribable<string>
    isShown: Subscribable<boolean>
    isSlidingMode: Subscribable<boolean>

    slidingLabelConfig: SlidingLabelConfig

    constructor(
        @inject(wccModules.componentConfig) configOrAttachment: WCCAttachmentCaptionVMConfig | WCCAttachment,
        @inject(wccModules.componentInfo) info: ko.components.ComponentInfo
    ) {
        const config = configOrAttachment instanceof WCCAttachment ? this.getConfig(configOrAttachment) : configOrAttachment;

        const isVisible = ko.flattenComputed(config.visible, true);

        this.attachment = ko.flattenComputed(config.attachment);
        this.placement = ko.flattenComputed(config.placement, 'bottom-left');
        this.mode = ko.flattenComputed(config.mode, 'title');
        this.value = ko.flattenComputed(config.value);

        this.isSlidingMode = ko.flattenComputed(config.slide, false);

        this.caption = ko.pureComputed(() => this.getCaption() ?? '');
        this.isShown = ko.pureComputed(() => isVisible() && this.caption().length > 0);

        this.slidingLabelConfig = { text: this.caption }

        ko.applyBindingsToNode(info.element, {
            'css': {
                'attachment-caption': true
            }
        });

        ko.applyBindingsToNode(info.element, {
            'css': this.placement.pluck(placement => `attachment-caption--placement-${placement}`)
        });
    }

    private getCaption() {
        const value = this.value();

        if (value != undefined)
            return value;

        const attachment = this.attachment();

        if (attachment != undefined) {
            switch (this.mode()) {
                case 'file-name':
                    if (attachment instanceof WCCFile)
                        return attachment.originalFileName();

                    break;                    
            }

            return attachment.viewTitle();
        }
    }

    private getConfig(attachment: WCCAttachment): WCCAttachmentCaptionVMConfig {
        return { attachment: attachment }
    }
}